import React, { useEffect, useRef, useState } from 'react';
import { useGuide } from './GuideContext';
import { cardPlacement, findGuideTarget, targetKeys } from './guideTarget';
import './GuideTour.css';

// The guided tour: a card that walks through one topic step by step, and a
// ring around the real control each step names.
//
// Built to survive the page changing under it, because it will: the teacher
// taps the ringed control, a sheet opens, the route changes, the layout
// reflows at a breakpoint. So the target is re-found on a short interval
// rather than once, and a step whose control is not on screen still shows its
// words plus a "where to find it" line. A missing target never breaks the
// tour; it only means no ring.
//
// The ring takes no clicks (pointer-events: none), so the teacher presses the
// real button underneath it. The card is always on screen, never hover-only,
// and moves to the top when the target is in the lower half.

const POLL_MS = 400;
const PAD = 6;

const sameRect = (a, b) =>
	a === b ||
	(a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

export default function GuideTour() {
	const guide = useGuide();
	const step = guide && guide.step;
	const [rect, setRect] = useState(null);
	const scrolledFor = useRef(null);

	useEffect(() => {
		if (!step) {
			setRect(null);
			return undefined;
		}
		const measure = () => {
			const el = findGuideTarget(step);
			if (!el) {
				setRect((prev) => (prev === null ? prev : null));
				return;
			}
			// Bring it into view once per step, and only if it is off screen.
			if (scrolledFor.current !== step) {
				scrolledFor.current = step;
				const r0 = el.getBoundingClientRect();
				if (r0.top < 0 || r0.bottom > window.innerHeight) {
					try {
						el.scrollIntoView({ block: 'center' });
					} catch (e) {
						/* jsdom and old Safari */
					}
				}
			}
			const r = el.getBoundingClientRect();
			const next = { top: r.top, left: r.left, width: r.width, height: r.height };
			setRect((prev) => (sameRect(prev, next) ? prev : next));
		};
		measure();
		const id = setInterval(measure, POLL_MS);
		window.addEventListener('resize', measure);
		window.addEventListener('scroll', measure, true);
		return () => {
			clearInterval(id);
			window.removeEventListener('resize', measure);
			window.removeEventListener('scroll', measure, true);
		};
	}, [step]);

	if (!step) return null;

	const { topic, steps, index } = guide;
	const isLast = index === steps.length - 1;
	const wantsTarget = targetKeys(step).length > 0;
	const placement = cardPlacement(rect, typeof window !== 'undefined' ? window.innerHeight : 800);

	return (
		<>
			{rect && (
				<div
					className='guide-ring'
					data-testid='guide-ring'
					aria-hidden='true'
					style={{
						top: rect.top - PAD,
						left: rect.left - PAD,
						width: rect.width + PAD * 2,
						height: rect.height + PAD * 2,
					}}
				/>
			)}
			<section
				className={`guide-card guide-card--${placement}`}
				role='dialog'
				aria-modal='false'
				aria-labelledby='guide-card-title'>
				<header className='guide-card__head'>
					<i className='fa-sharp fa-solid fa-signs-post guide-card__icon' aria-hidden='true' />
					<div className='guide-card__titles'>
						<h2 className='guide-card__title' id='guide-card-title'>
							{topic.title}
						</h2>
						<p className='guide-card__count'>
							Step {index + 1} of {steps.length}
						</p>
					</div>
				</header>

				<p className='guide-card__text' aria-live='polite'>
					{step.text}
				</p>

				{wantsTarget && !rect && step.where && (
					<p className='guide-card__where' data-testid='guide-where'>
						<i className='fa-sharp fa-solid fa-circle-info' aria-hidden='true' />
						<span>
							<strong>Can’t see it here?</strong> {step.where}
						</span>
					</p>
				)}

				<div className='guide-card__actions'>
					{index > 0 && (
						<button type='button' className='ps-btn guide-card__btn' onClick={guide.back}>
							<i className='fa-sharp fa-solid fa-arrow-left' aria-hidden='true' />
							Back
						</button>
					)}
					{isLast ? (
						<button type='button' className='ps-btn ps-btn--primary guide-card__btn' onClick={guide.end}>
							<i className='fa-sharp fa-solid fa-check' aria-hidden='true' />
							Done
						</button>
					) : (
						<button type='button' className='ps-btn ps-btn--primary guide-card__btn' onClick={guide.next}>
							Next
							<i className='fa-sharp fa-solid fa-arrow-right' aria-hidden='true' />
						</button>
					)}
					{!isLast && (
						<button type='button' className='guide-card__end' onClick={guide.end}>
							End tour
						</button>
					)}
				</div>
			</section>
		</>
	);
}
